import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, Stack, IconButton, Tooltip,
  LinearProgress, useTheme, useMediaQuery, alpha, keyframes,
} from '@mui/material';

import Iconify from '../../components/Iconify';
// The same measurement the engine uses to decide a target has painted. Its own
// copy here used querySelector, which takes the first match even when that is
// the hidden one of a phone/desktop pair, so the ring and the card could end up
// pointing at nothing on a step the engine considered perfectly fine.
import { readRect } from './dom';

// ---------------------------------------------------------------------------
// The spotlight itself. Fully controlled: the provider owns run state and every
// callback, this renders one step.
//
// The important difference from the old SpotlightTour is the hole. There, one
// full-viewport rect sat over the page and swallowed every click, so a step
// could only ever say "look at this". Here, on an acting step, the dimmer is
// inert and four blockers are drawn *around* the hole instead - so the real
// button underneath is genuinely clickable and everything else is not. That is
// both the teaching device and the guardrail.
// ---------------------------------------------------------------------------

const CARD_WIDTH = 380;
const PAD = 8;   // breathing room around the highlighted element
const GAP = 14;  // distance between the element and the card

const pulse = keyframes`
  0%   { opacity: 1;   transform: scale(1); }
  50%  { opacity: 0.4; transform: scale(0.82); }
  100% { opacity: 1;   transform: scale(1); }
`;

const nudge = keyframes`
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-6px); }
  40%      { transform: translateX(6px); }
  60%      { transform: translateX(-4px); }
  80%      { transform: translateX(4px); }
`;

const ringPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 currentColor; }
  70%  { box-shadow: 0 0 0 10px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

export default function CoachOverlay({
  guideTitle,
  step,
  stepIndex,
  total,
  status,
  paused,
  onNext,
  onBack,
  onSkipStep,
  onDoItForMe,
  onPause,
  onResume,
  onExit,
  onRetry,
}) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const cardRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(200);
  const [nudging, setNudging] = useState(false);

  const target = step?.target;

  // Keep the rect fresh. Targets lazy-load, dialogs animate in, tables reflow.
  useEffect(() => {
    if (!step || paused) return undefined;
    const update = () => setRect(readRect(target));
    update();
    const id = setInterval(update, 120);
    const onMove = () => update();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('transitionend', onMove, true);
    return () => {
      clearInterval(id);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('transitionend', onMove, true);
    };
  }, [step, target, paused]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [stepIndex, rect, step, status, isPhone]);

  // Esc pauses rather than quitting: losing a half-finished setup to a stray
  // key press is a worse outcome than an extra click to resume.
  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); if (paused) onResume(); else onPause(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, paused, onPause, onResume]);

  if (!step) return null;

  const primary = theme.palette.primary.main;
  const Z = theme.zIndex.modal + 40;

  // ----- paused: collapse to a pill so the user can go do something else -----
  if (paused) {
    return (
      <Box
        sx={{
          position: 'fixed', left: 16, bottom: 16, zIndex: Z,
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1, borderRadius: 999,
          bgcolor: 'background.paper',
          border: `1px solid ${alpha(primary, 0.24)}`,
          boxShadow: `0 12px 32px -12px ${alpha(theme.palette.common.black, 0.45)}`,
        }}
      >
        <Box
          sx={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${primary}, ${alpha(primary, 0.65)})`,
          }}
        >
          <Iconify icon="mdi:compass-outline" sx={{ width: 15, height: 15, color: '#fff' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {guideTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Paused at step {stepIndex + 1} of {total}
          </Typography>
        </Box>
        <Button size="small" variant="contained" disableElevation onClick={onResume} sx={{ borderRadius: 999, fontWeight: 700, ml: 0.5 }}>
          Resume
        </Button>
        <IconButton size="small" onClick={onExit} aria-label="Leave guide">
          <Iconify icon="eva:close-fill" sx={{ width: 16, height: 16 }} />
        </IconButton>
      </Box>
    );
  }

  const kind = step.kind || 'narrate';
  const isActing = kind === 'act' && status === 'active';
  // An awaiting step has no single control to point at: the user is filling a
  // form and the guide is listening for the save. So the overlay steps out of
  // the way completely - no dim, no blockers, only the card. Blocking here is
  // the one thing that would break do-it-live outright, because what we are
  // waiting for is a click we would otherwise swallow.
  const isAwaiting = kind === 'await' && status === 'active';
  const isWaiting = status === 'waiting' || status === 'navigating' || kind === 'await';
  const isStalled = status === 'stalled';
  const isLast = stepIndex === total - 1;

  const hole = rect
    ? { x: rect.left - PAD, y: rect.top - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 }
    : null;

  // On an acting step the hole must be live, so the dimmer goes inert and we
  // draw the click blockers around it instead of over it.
  const interactive = isActing && !!hole;

  const bumpNudge = () => {
    setNudging(true);
    setTimeout(() => setNudging(false), 450);
  };

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(CARD_WIDTH, vw - 24);
  const placement = step.placement || 'bottom';

  let top;
  let left;
  if (!rect || placement === 'center') {
    left = (vw - cardW) / 2;
    top = (vh - cardH) / 2;
  } else if (placement === 'right') {
    left = rect.right + GAP;
    top = rect.top + rect.height / 2 - cardH / 2;
  } else if (placement === 'left') {
    left = rect.left - cardW - GAP;
    top = rect.top + rect.height / 2 - cardH / 2;
  } else if (placement === 'top') {
    top = rect.top - cardH - GAP;
    left = rect.left + rect.width / 2 - cardW / 2;
  } else {
    top = rect.bottom + GAP;
    left = rect.left + rect.width / 2 - cardW / 2;
  }
  left = Math.max(12, Math.min(left, vw - cardW - 12));
  top = Math.max(12, Math.min(top, vh - cardH - 12));

  // The blockers and the card are siblings, so the card has to sit above them
  // explicitly. Positioned elements with an auto z-index paint under any
  // sibling carrying a positive one whatever the DOM order says, which put the
  // full-screen blocker on top of the card: every click on Next hit the
  // blocker, which nudges the card and does nothing else.
  const blockerSx = {
    position: 'fixed', pointerEvents: 'auto', zIndex: Z,
  };
  const CARD_Z = Z + 1;

  // While awaiting, the card gets out of the middle of the screen: the user is
  // working a form underneath it and a centred card would sit on top of the
  // very fields we just told them to fill in.
  let cardPosition;
  if (isPhone) {
    cardPosition = { left: 12, right: 12, bottom: 12, width: 'auto', maxWidth: 'none' };
  } else if (isAwaiting) {
    cardPosition = { right: 20, bottom: 20, width: cardW };
  } else {
    cardPosition = { left, top, width: cardW };
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: Z, pointerEvents: 'none' }}>
      {/* Dimmer. Always inert - clicks are governed by the blockers below. */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}
        aria-hidden
      >
        <defs>
          <mask id="deskos-coach-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx="12" ry="12" fill="black" />}
          </mask>
        </defs>
        {!isAwaiting && (
          <rect
            x="0" y="0" width="100%" height="100%"
            fill={alpha('#0F172A', 0.62)}
            mask="url(#deskos-coach-mask)"
          />
        )}
      </svg>

      {/* Click blockers. Four around the hole when one control must stay live,
          one over everything when the step is only asking to be read, and none
          at all while we wait on a form the user has to fill in. */}
      {!isAwaiting && interactive && (
        <>
          <Box onClick={bumpNudge} sx={{ ...blockerSx, left: 0, top: 0, right: 0, height: Math.max(0, hole.y) }} />
          <Box onClick={bumpNudge} sx={{ ...blockerSx, left: 0, top: hole.y + hole.h, right: 0, bottom: 0 }} />
          <Box onClick={bumpNudge} sx={{ ...blockerSx, left: 0, top: hole.y, width: Math.max(0, hole.x), height: hole.h }} />
          <Box onClick={bumpNudge} sx={{ ...blockerSx, left: hole.x + hole.w, top: hole.y, right: 0, height: hole.h }} />
        </>
      )}
      {!isAwaiting && !interactive && <Box onClick={bumpNudge} sx={{ ...blockerSx, inset: 0 }} />}

      {/* The ring. Decorative only, never intercepts. */}
      {hole && (
        <Box
          sx={{
            position: 'fixed',
            left: hole.x, top: hole.y, width: hole.w, height: hole.h,
            borderRadius: '12px',
            border: `2px solid ${primary}`,
            color: alpha(primary, 0.35),
            boxShadow: `0 0 0 4px ${alpha(primary, 0.22)}`,
            pointerEvents: 'none',
            zIndex: Z,
            transition: 'left .2s ease, top .2s ease, width .2s ease, height .2s ease',
            ...(isActing && { animation: `${ringPulse} 1.6s ease-out infinite` }),
          }}
        />
      )}

      {/* The card */}
      <Box
        ref={cardRef}
        role="dialog"
        aria-live="polite"
        aria-label={`${guideTitle}, step ${stepIndex + 1} of ${total}`}
        sx={{
          position: 'fixed',
          pointerEvents: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: `0 24px 60px -12px ${alpha(theme.palette.common.black, 0.45)}`,
          overflow: 'hidden',
          zIndex: CARD_Z,
          transition: 'left .22s ease, top .22s ease',
          ...cardPosition,
          ...(nudging && { animation: `${nudge} .45s ease` }),
        }}
      >
        <LinearProgress
          variant="determinate"
          value={((stepIndex + (isLast ? 1 : 0)) / total) * 100}
          sx={{ height: 3, bgcolor: alpha(primary, 0.12) }}
        />

        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: primary, fontWeight: 700, letterSpacing: 0.6, flex: 1 }} noWrap>
              {guideTitle} · {stepIndex + 1}/{total}
            </Typography>
            <Tooltip title="Pause. You can pick this back up from the same step.">
              <IconButton size="small" onClick={onPause} aria-label="Pause guide">
                <Iconify icon="mdi:pause" sx={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Leave the guide">
              <IconButton size="small" onClick={onExit} aria-label="Leave guide">
                <Iconify icon="eva:close-fill" sx={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 800, lineHeight: 1.3 }}>
            {step.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
            {step.body}
          </Typography>

          {/* Status strip: what the guide is currently waiting for. */}
          {isStalled && (
            <Stack
              direction="row" alignItems="flex-start" spacing={1}
              sx={{
                mt: 1.75, p: 1.25, borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
              }}
            >
              <Iconify icon="mdi:map-marker-question-outline" sx={{ width: 18, height: 18, mt: 0.2, color: 'warning.darker', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: 'warning.darker', lineHeight: 1.5 }}>
                We could not find that on this screen. It may be hidden by your permissions or your plan.
              </Typography>
            </Stack>
          )}

          {!isStalled && isActing && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: primary, animation: `${pulse} 1.4s ease-in-out infinite` }} />
              <Typography variant="caption" sx={{ color: primary, fontWeight: 700 }}>
                {step.waitingLabel || 'Your turn. Go ahead and do it.'}
              </Typography>
            </Stack>
          )}

          {!isStalled && !isActing && isWaiting && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', animation: `${pulse} 1.4s ease-in-out infinite` }} />
              <Typography variant="caption" color="text.secondary">
                {status === 'navigating' && 'Taking you there...'}
                {status !== 'navigating' &&
                  (isAwaiting ? step.waitingLabel || 'Go ahead, we are watching for it.' : 'Waiting for the screen...')}
              </Typography>
            </Stack>
          )}

          {/* Actions */}
          <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
            {stepIndex > 0 && (
              <Button size="small" onClick={onBack} sx={{ color: 'text.secondary', mr: 0.5 }}>
                Back
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />

            {isStalled && (
              <>
                <Button size="small" onClick={onRetry} sx={{ color: 'text.secondary', mr: 1 }}>
                  Try again
                </Button>
                <Button size="small" variant="contained" disableElevation onClick={onSkipStep} sx={{ borderRadius: 2, fontWeight: 700 }}>
                  Skip this step
                </Button>
              </>
            )}

            {!isStalled && isActing && (
              <>
                <Button size="small" onClick={onSkipStep} sx={{ color: 'text.secondary', mr: step.doItForMe ? 1 : 0 }}>
                  Skip step
                </Button>
                {step.doItForMe && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onDoItForMe}
                    startIcon={<Iconify icon="mdi:cursor-default-click-outline" />}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Do it for me
                  </Button>
                )}
              </>
            )}

            {!isStalled && !isActing && kind === 'await' && (
              <Button size="small" onClick={onSkipStep} sx={{ color: 'text.secondary' }}>
                Skip step
              </Button>
            )}

            {!isStalled && kind === 'narrate' && status === 'active' && (
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={onNext}
                endIcon={!isLast && <Iconify icon="eva:arrow-ios-forward-fill" />}
                sx={{ borderRadius: 2, fontWeight: 700, px: 2 }}
              >
                {isLast ? 'Finish' : 'Next'}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

CoachOverlay.propTypes = {
  guideTitle: PropTypes.string,
  step: PropTypes.object,
  stepIndex: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  status: PropTypes.oneOf(['navigating', 'waiting', 'active', 'stalled']).isRequired,
  paused: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func.isRequired,
  onDoItForMe: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};
